import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { RagAgent } from "../api/ragApi";
import { fetchAgentsThunk, getRagAgents, getRagAgentsStatus } from "../state/ragSlice";
import { SearchInput } from "src/common/components/SearchInput";
import WixarBadge from "./WixarBadge";
import { CheckIcon, PlusIcon } from "@heroicons/react/outline";
import CreateAgentModal from "./CreateAgentModal";

const AGENT_ICONS: Record<string, string> = {
  default: "\uD83E\uDD16",
  book: "\uD83D\uDCDA",
  support: "\uD83C\uDFA7",
  brain: "\uD83E\uDDE0",
  search: "\uD83D\uDD0D",
  chat: "\uD83D\uDCAC",
  star: "\u2B50",
  tool: "\uD83D\uDD27",
  idea: "\uD83D\uDCA1",
  language: "\uD83C\uDF0D",
  education: "\uD83C\uDF93",
  expert: "\uD83D\uDC54",
};

interface RagAgentMultiSelectProps {
  value: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  title?: string;
  description?: string;
  /**
   * When true, the agent list grows to fill the parent's available height
   * (and scrolls internally) instead of being capped at a fixed 200px.
   * The parent must be a flex column with a definite height for this to work.
   * Defaults to false so existing call sites keep the fixed-height behaviour.
   */
  fillHeight?: boolean;
}

const RagAgentMultiSelect = ({
  value,
  onChange,
  disabled = false,
  title,
  description,
  fillHeight = false,
}: RagAgentMultiSelectProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const agents = useAppSelector(getRagAgents);
  const status = useAppSelector(getRagAgentsStatus);
  const loading = agents.length === 0 && (status === "idle" || status === "loading");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    void dispatch(fetchAgentsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (agents.length === 0 || value.length === 0) return;
    const existingIds = new Set(agents.map((a) => a.id));
    const validIds = value.filter((id) => existingIds.has(id));
    if (validIds.length !== value.length) {
      onChange(validIds);
    }
  }, [agents, value]);

  const toggleAgent = (agentId: number) => {
    if (disabled) return;
    const newValue = value.includes(agentId)
      ? value.filter((id) => id !== agentId)
      : [...value, agentId];
    onChange(newValue);
  };

  const { ownAgents, sharedAgents } = useMemo(() => {
    let filtered = agents;
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = agents.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          (a.description && a.description.toLowerCase().includes(q)),
      );
    }
    const sortFn = (a: RagAgent, b: RagAgent) => a.name.localeCompare(b.name);
    return {
      ownAgents: filtered.filter((a) => !a.is_shared).sort(sortFn),
      sharedAgents: filtered.filter((a) => a.is_shared).sort(sortFn),
    };
  }, [agents, search]);

  const renderAgentButton = (agent: RagAgent) => {
    const isSelected = value.includes(agent.id);
    return (
      <button
        key={agent.id}
        type="button"
        onClick={() => toggleAgent(agent.id)}
        disabled={disabled}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all text-sm flex-shrink-0 w-full ${
          isSelected
            ? "border-green-400 bg-green-50 shadow-sm"
            : "border-gray-200 bg-white hover:border-gray-300"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span className="text-lg flex-shrink-0">
          {AGENT_ICONS[agent.icon] || AGENT_ICONS.default}
        </span>
        <div className="flex-1 min-w-0">
          <span className="font-medium text-gray-900 truncate flex items-center gap-1">
            {agent.name}
            {agent.is_shared && <WixarBadge size="sm" />}
          </span>
          {(agent.description || agent.document_count != null) && (
            <span className="text-xs text-gray-400 truncate block">
              {agent.description ||
                t("pages.agents.documentCount", { count: agent.document_count ?? 0 })}
            </span>
          )}
        </div>
        <div
          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
            isSelected ? "border-green-500 bg-green-500" : "border-gray-300"
          }`}
        >
          {isSelected && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} />}
        </div>
      </button>
    );
  };

  const createAgentButton = !disabled && (
    <button
      type="button"
      onClick={() => setCreateOpen(true)}
      className="inline-flex items-center gap-1.5 text-xs text-green-600 hover:text-green-700 font-medium transition-colors flex-shrink-0 whitespace-nowrap"
    >
      <PlusIcon className="h-3.5 w-3.5" strokeWidth={2} />
      {t("pages.agents.ragAgentSelect.createAgent")}
    </button>
  );

  const createAgentModal = (
    <CreateAgentModal
      isOpen={createOpen}
      onClose={() => setCreateOpen(false)}
      onCreated={(agent) => onChange([...value, agent.id])}
    />
  );

  if (loading) {
    return (
      <div className="mt-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          {title || t("pages.agents.ragAgentSelect.title")}
        </h3>
        <div className="animate-pulse flex flex-col gap-2">
          <div className="h-10 bg-gray-200 rounded-lg" />
          <div className="h-10 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="mt-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          {title || t("pages.agents.ragAgentSelect.title")}
        </h3>
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-gray-400">
            {t("pages.agents.ragAgentSelect.noAgentsAvailable")}
          </p>
          {createAgentButton}
        </div>
        {createAgentModal}
      </div>
    );
  }

  const noResults = ownAgents.length === 0 && sharedAgents.length === 0 && search.trim();
  const totalVisible = ownAgents.length + sharedAgents.length;
  const needsScroll = totalVisible > 3;

  return (
    <div className={`mt-4 min-w-0 ${fillHeight ? "flex flex-col flex-1 min-h-0" : ""}`}>
      <h3 className="text-sm font-medium text-gray-700 mb-1">
        {title || t("pages.agents.ragAgentSelect.title")}
      </h3>
      <p className="text-xs text-gray-400 mb-3">
        {description || t("pages.agents.ragAgentSelect.description")}
      </p>

      {agents.length > 5 && (
        <div className="mb-2">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("pages.agents.ragAgentSelect.searchPlaceholder")}
          />
        </div>
      )}

      <div
        className={`flex flex-col gap-1.5 ${
          fillHeight
            ? "flex-1 min-h-0 overflow-y-auto"
            : needsScroll
            ? "h-[200px] overflow-y-auto"
            : ""
        }`}
      >
        {sharedAgents.length > 0 ? (
          <>
            <div className="flex items-center justify-between gap-2 pt-1 flex-shrink-0">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {t("pages.agents.ragAgentSelect.sharedAgents")}
              </p>
              {createAgentButton}
            </div>
            {sharedAgents.map(renderAgentButton)}
          </>
        ) : (
          createAgentButton && (
            <div className="flex justify-end pt-1 flex-shrink-0">{createAgentButton}</div>
          )
        )}

        {ownAgents.length > 0 && (
          <>
            {sharedAgents.length > 0 && (
              <div className="border-t border-gray-200 my-1 flex-shrink-0" />
            )}
            {sharedAgents.length > 0 && (
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-1 flex-shrink-0">
                {t("pages.agents.ragAgentSelect.yourAgents")}
              </p>
            )}
            {ownAgents.map(renderAgentButton)}
          </>
        )}

        {noResults && (
          <p className="text-sm text-gray-400 text-center py-3">{t("general.noResults")}</p>
        )}
      </div>

      {value.length > 0 && (
        <p className="text-xs text-gray-500 mt-2">
          {t("pages.agents.ragAgentSelect.selectedCount", { count: value.length })}
        </p>
      )}
      {createAgentModal}
    </div>
  );
};

export default RagAgentMultiSelect;
